import React, { JSX, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import BenchmarkingServiceAreaFilters from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingServiceAreaFilters";
import NetworkFlyoutCountiesTable from "@app/overviews/network-benchmarking-dashboard/filters/NetworkFlyoutCountiesTable";
import NetworkFlyoutPlansTable from "@app/overviews/network-benchmarking-dashboard/filters/NetworkFlyoutPlansTable";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { useQHPContractDetails } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/hooks/useQHPContractDetails";
import { QHPFiltersContext } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPFiltersContext";
import QHPMarketCoverageBadges from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPMarketCoverageBadges";
import { QHPContractPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPContractPrimaryFiltersPayload";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";

interface QHPNetworkFlyoutProps {
  contract: QHPNetwork;
}

const QHPNetworkFlyout = (props: QHPNetworkFlyoutProps): JSX.Element => {
  const [isYourNetwork, setIsYourNetwork] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const qhpFiltersContext = useContext(QHPFiltersContext);
  const userContext = useContext(UserContext);

  useEffect(() => {
    if (!!props.contract?.contractId) {
      const yourNetworks: QHPNetwork[] = qhpFiltersContext.parentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
      setIsYourNetwork((yourNetworks.map(x => x.contractId).includes(props.contract?.contractId)));
    }
  }, [props.contract?.contractId]);

  const contractPayload = (): QHPContractPrimaryFiltersPayload | null => {
    if (!props.contract?.contractId || !benchmarkingFiltersContext.displayedPrimaryFilters) {
      return null;
    }

    const payload = cloneDeep(benchmarkingFiltersContext.displayedPrimaryFilters) as QHPContractPrimaryFiltersPayload;
    payload.contractId = props.contract.contractId;
    payload.versionId = props.contract.versionId;

    if (payload.starRatings.length === qhpFiltersContext.mappedStarRatings.length) {
      payload.starRatings = [];
    }

    return payload;
  }

  const { contractDetails, isLoading: loading } = useQHPContractDetails(contractPayload());

  const contract = useMemo(() => {
    if (!contractDetails) return null;

    return {
      ...cloneDeep(props.contract),
      counties: contractDetails.counties,
      plans: contractDetails.plans,
    };
  }, [contractDetails, props.contract]);

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-md-12">
          <div>
            <span className="demibold">Payer Name:</span> {props.contract?.parentOrganization}
          </div>
          <div>
            <span className="demibold">Issuer ID:</span> {props.contract?.issuerId}
          </div>
          <div>
            <span className="demibold">Network Name:</span> {props.contract?.networkName}
          </div>
          <div>
            <span className="demibold">Market Coverage:</span> <QHPMarketCoverageBadges marketCoverage={props.contract?.marketCoverage} />
          </div>
        </div>
      </div>

      <BenchmarkingServiceAreaFilters />

      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          ) : (
            <AccordionContainer>
              <Accordion
                title={`${!isYourNetwork ? "Active " : ""}Counties`}
                testId="h-contract-flyout__counties-accordion"
              >
                <NetworkFlyoutCountiesTable
                  contract={contract}
                  data={contract?.counties}
                  isYourNetwork={isYourNetwork}
                  loading={loading}
                  onApplyServiceAreaClick={qhpFiltersContext.onApplyServiceAreaClick}
                />
              </Accordion>

              <Accordion
                title="Plans"
                testId="h-contract-flyout__plans-accordion"
              >
                <NetworkFlyoutPlansTable
                  contractId={contract?.contractId}
                  data={contract?.plans}
                />
              </Accordion>

              {(userContext.roles.includes(Roles.CLIENT_ADMIN) || isYourNetwork) && (
                <Accordion
                  title="Data Details"
                  testId="h-contract-flyout__data-details"
                >
                  <div className="row">
                    <div className="col-md-12">
                      <label className="label-m">
                        QES Data
                      </label>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="demibold">
                        {props.contract?.isSupersetNetwork ? "Superset Project Id:" : "QES Project Id:"}
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.projectId ?
                        <Link to={`/projects/${props.contract.projectId}`} target="_blank">{props.contract.projectId}</Link> :
                        "---"
                      }
                    </div>
                  </div>
                </Accordion>
              )}
            </AccordionContainer>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default QHPNetworkFlyout;
